<template>
    <div class="relative bg-black mb-10">
        <div class="absolute top-0 left-0 w-full h-full">
            <nuxt-img :src="backgroundImageSrc" sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw" preload
                fetchpriority="high" loading="eager" width="1920" height="1018" :alt="backgroundImageAlt" format="webp"
                class="object-cover w-full h-full" />
        </div>
        <div class="relative z-10 md:px-6 sm:px-4 px-6 pt-6 mx-auto max-w-screen-2xl">
            <div class="flex z-20 gap-2 justify-between items-center sm:gap-4 lg:gap-x-8">
                <div class="relative">
                    <a href="/">
                        <nuxt-img :src="logoSrc" :alt="logoAlt" width="167" height="64"
                            class="object-contain relative z-10 w-28 md:w-40" />
                    </a>
                </div>


                <div class="flex sm:gap-x-2 md:gap-x-4">

                    <div class="sm:flex hidden">
                        <!-- Phone number - hidden on mobile -->
                        <a :href="`tel:${phoneNumber}`"
                            class="flex gap-2 items-center px-4 py-2 text-sm md:text-base text-white rounded-full transition xl:px-4 lg:px-0 sm:py-2.5 font-ww-medium">
                            <svg class="text-white us-max:hidden" width="25" height="26" viewBox="0 0 25 26" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M15.5586 4.67578C16.8831 5.03125 18.0908 5.72885 19.0606 6.69857C20.0303 7.66829 20.7279 8.87602 21.0833 10.2005"
                                    stroke="currentColor" stroke-width="1.56177" stroke-linecap="round"
                                    stroke-linejoin="round" />
                                <path
                                    d="M14.748 7.69141C15.5447 7.90297 16.2712 8.32137 16.854 8.90419C17.4368 9.48701 17.8552 10.2135 18.0668 11.0102"
                                    stroke="currentColor" stroke-width="1.56177" stroke-linecap="round"
                                    stroke-linejoin="round" />
                                <path
                                    d="M9.0308 12.9532C9.83435 14.6126 11.1762 15.951 12.8376 16.7503C12.9602 16.8083 13.0957 16.8334 13.2309 16.8232C13.3661 16.8129 13.4963 16.7676 13.6087 16.6917L16.049 15.0616C16.1568 14.9885 16.2814 14.9439 16.4111 14.9319C16.5408 14.92 16.6715 14.9411 16.7908 14.9933L21.359 16.9553C21.5151 17.0202 21.6456 17.1347 21.7302 17.2811C21.8149 17.4275 21.8491 17.5977 21.8275 17.7654C21.6827 18.8955 21.1311 19.9341 20.276 20.6869C19.4208 21.4397 18.3206 21.855 17.1813 21.8553C13.6605 21.8553 10.284 20.4567 7.79441 17.9671C5.30486 15.4776 3.90625 12.101 3.90625 8.58028C3.90651 7.44097 4.32189 6.34078 5.07467 5.48559C5.82746 4.6304 6.86605 4.07881 7.99613 3.93402C8.16386 3.91247 8.33403 3.94666 8.48042 4.03132C8.62681 4.11599 8.74131 4.24642 8.8063 4.40255L10.7683 8.98048C10.8193 9.09795 10.8406 9.22617 10.8304 9.35384C10.8202 9.4815 10.7787 9.60469 10.7097 9.71256L9.07961 12.1919C9.00703 12.304 8.96444 12.4329 8.95589 12.5662C8.94735 12.6995 8.97314 12.8327 9.0308 12.9532Z"
                                    stroke="currentColor" stroke-width="1.56177" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                            <span class="contents"><span class="hidden md:block">{{ phoneLabel }}</span>
                                {{ phoneNumber }}</span></a>
                    </div>

                    <button @click="scrollTo(buttonScrollTarget, 'bottom')"
                        class="flex items-center px-4 py-2.5 mx-auto text-base font-bold rounded-full lg:px-8 font-ww-medium btn-red gap-x-2 sm:px-3 sm:py-3 hover:bg-[#f3695e] transition-colors duration-200">
                        {{ buttonText }}
                        <svg class="w-4" width="21" height="15" viewBox="0 0 21 15" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12.8593 0.338767C12.7655 0.43173 12.6911 0.542331 12.6404 0.66419C12.5896 0.78605 12.5635 0.916755 12.5635 1.04877C12.5635 1.18078 12.5896 1.31148 12.6404 1.43334C12.6911 1.5552 12.7655 1.6658 12.8593 1.75877L17.4593 6.35877L1.05859 6.17123C0.793377 6.17123 0.539023 6.27659 0.351487 6.46412C0.163951 6.65166 0.0585938 6.90601 0.0585938 7.17123C0.0585938 7.43645 0.163951 7.6908 0.351487 7.87834C0.539023 8.06587 0.793377 8.17123 1.05859 8.17123L17.4393 8.35877L12.8593 12.9288C12.673 13.1161 12.5685 13.3696 12.5685 13.6338C12.5685 13.898 12.673 14.1514 12.8593 14.3388C13.0466 14.525 13.3001 14.6296 13.5643 14.6296C13.8284 14.6296 14.0819 14.525 14.2693 14.3388L20.6293 7.97877C20.7149 7.89664 20.783 7.79805 20.8295 7.68892C20.876 7.5798 20.9 7.4624 20.9 7.34377C20.9 7.22514 20.876 7.10773 20.8295 6.99861C20.783 6.88949 20.7149 6.7909 20.6293 6.70877L14.2793 0.338767C14.1863 0.245039 14.0757 0.170645 13.9538 0.119876C13.832 0.0691072 13.7013 0.0429688 13.5693 0.0429688C13.4372 0.0429688 13.3065 0.0691072 13.1847 0.119876C13.0628 0.170645 12.9522 0.245039 12.8593 0.338767Z"
                                fill="white"></path>
                        </svg>
                    </button>
                </div>

            </div>
        </div>

        <div class="flex relative z-10 flex-col pt-10 mx-auto xl:pt-0 lg:pb-20 lg:flex-row md-max:pt-6">
            <div class="lg:ml-[calc((100vw_-_1024px)_/_2)] mx-auto xl:px-0 px-4">
                <div class="max-w-3xl lg:max-w-xl xl:max-w-2xl">
                    <div class="xl:pr-10">
                        <div
                            class="text-center text-[#C4E538] xl:text-2xl text-lg sm:text-xl font-ww-medium lg:text-left font-bold">
                            {{ attentionText }}
                        </div>

                        <h1 class="mt-2 lg:!leading-none leading-8 text-center text-white text-3xl sm:text-4xl lg:text-left xl:text-45 font-ww-medium"
                            v-html="mainHeadline">
                        </h1>

                        <p class="mt-5 text-lg text-white sm:text-xl xl:text-2xl !leading-6 font-ww-light lg:text-left font-semibold"
                            v-html="introParagraph">
                        </p>

                        <ul
                            class="mt-4 space-y-2 text-base text-white sm:text-lg xl:text-lg font-ww-light lg:text-left">
                            <li v-for="(problem, index) in problems" :key="index" class="flex items-start !leading-5">
                                <span class="mr-3 text-white">•</span>
                                <span class="" v-html="problem"></span>
                            </li>
                        </ul>
                    </div>

                    <div class="bg-[#C4E538] relative pt-4 px-5 pb-6 mt-7 xl:mr-20 2xl:mr-16">


                        <div class="">
                            <div class="pt-3">
                                <div class="text-base !leading-6 md-max:!leading-5 text-[#1C1C1C] sm:text-base xl:text-17 font-ww-regular font-light"
                                    v-html="userComment">
                                </div>
                                <div class="mt-4 text-sm sm:text-base text-[#1C1C1C] font-ww-medium not-italic"
                                    v-html="userCommentAuthor">
                                </div>
                            </div>
                        </div>

                        <img src="/assets/images/bg-right-mindflow.png" width="159" height="217" alt="hero image"
                            class="object-cover absolute right-0 -bottom-0 lg:h-[217px] h-full opacity-60">

                    </div>
                </div>
            </div>

            <nuxt-img :src="heroImageSrc" sizes="xs:334px sm:425px md:751px lg:967px xl:967px" preload
                fetchpriority="high" loading="eager" width="967" height="720" :alt="heroImageAlt" format="webp"
                class="right-0 -bottom-20 lg:mb-0 -mb-12 lg:mx-auto lg:mr-auto mr-0 lg:ml-0 ml-auto lg:absolute 3xl:w-[967px] 2xl:w-6/12 lg:w-[54%] lg:pt-0 pt-10" />
        </div>
    </div>
</template>

<script setup>
defineProps({
    backgroundImageSrc: {
        type: String,
        default: '/assets/images/bg-hero-mindflow-new-1.jpg'
    },
    backgroundImageAlt: {
        type: String,
        default: 'hero background'
    },
    logoSrc: {
        type: String,
        default: '/assets/images/horizontal-logo.png'
    },
    logoAlt: {
        type: String,
        default: 'logo'
    },
    phoneNumber: {
        type: String,
        default: '1-800-479-1596'
    },
    phoneLabel: {
        type: String,
        default: 'Call Us Toll Free'
    },
    attentionText: {
        type: String,
        default: 'ATTENTION COFFEE DRINKERS'
    },
    mainHeadline: {
        type: String,
        default: 'The "Clean Energy" Secret: How to Support Steady, Calm Focus (Without The Jitters or The Crash)'
    },
    introParagraph: {
        type: String,
        default: 'Most people think "brain fog" is just a lack of caffeine. It’s not. It’s usually a lack of support. Here is the smarter way to fuel your morning.'
    },
    problems: {
        type: Array,
        default: () => [
            '<strong>The "Digital Drain":</strong> Why modern distractions leave you feeling mentally exhausted by noon—and why standard stimulation isn\'t the answer.',
            '<strong>The "Dirty Energy" Trap:</strong> Why sugary drinks and high-acid coffees spike your energy fast, only to drop you even harder (and how to step off the rollercoaster).',
            '<strong>The "Over-Caffeinated" Spiral:</strong> How to spot the difference between being wired and being focused—and the simple switch that supports calm clarity.'
        ]
    },
    truthLabel: {
        type: String,
        default: 'Here\'s the truth:'
    },
    truthText: {
        type: String,
        default: 'Your brain isn\'t broken. It just needs better fuel.'
    },
    descriptionText: {
        type: String,
        default: 'Neuroflow is a revolutionary nootropic mushroom coffee that\'s been scientifically designed to help you think faster, focus longer, and feel mentally unstoppable—without the crash.'
    },
    userComment: {
        type: String,
        default: '"Mindflow is a great start to the day. It left me energized but also focused, without the jittery feeling of lots of coffees. I appreciate that Lifeboost has added electrolytes, adaptogens, and collagen for the optimal morning beverage. It mixes into water very easily and has a smooth taste! I would recommend it to anyone looking to level up their morning coffee game."'
    },
    userCommentAuthor: {
        type: String,
        default: '— Jenica Weaver DPT, ATC'
    },
    heroImageSrc: {
        type: String,
        default: '/assets/images/image1-Photoroom.png'
    },
    heroImageAlt: {
        type: String,
        default: 'LIFEBOOST MINDLFOW INSTANT COFFEE product'
    },
    buttonText: {
        type: String,
        default: 'ORDER NOW'
    },
    buttonScrollTarget: {
        type: String,
        default: 'products'
    }
})

defineBuilderComponent({
    friendlyName: 'Intro Text With Image Hero',
    canHaveChildren: false,
    inputs: [
        {
            name: 'backgroundImageSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            defaultValue: '/assets/images/bg-hero-mindflow-new-1.jpg'
        },
        {
            name: 'backgroundImageAlt',
            type: 'text',
            defaultValue: 'hero background'
        },
        {
            name: 'logoSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
            defaultValue: '/assets/images/horizontal-logo.png'
        },
        {
            name: 'logoAlt',
            type: 'text',
            defaultValue: 'logo'
        },
        {
            name: 'phoneNumber',
            type: 'text',
            defaultValue: '1-800-479-1596'
        },
        {
            name: 'phoneLabel',
            type: 'text',
            defaultValue: 'Call Us Toll Free'
        },
        {
            name: 'attentionText',
            type: 'richText',
            defaultValue: 'ATTENTION COFFEE DRINKERS'
        },
        {
            name: 'mainHeadline',
            type: 'richText',
            defaultValue: 'The "Clean Energy" Secret: How to Support Steady, Calm Focus (Without The Jitters or The Crash)'
        },
        {
            name: 'introParagraph',
            type: 'richText',
            defaultValue: 'Most people think "brain fog" is just a lack of caffeine. It’s not. It’s usually a lack of support. Here is the smarter way to fuel your morning.'
        },
        {
            name: 'problems',
            type: 'list',
            defaultValue: [
                '<strong>The "Digital Drain":</strong> Why modern distractions leave you feeling mentally exhausted by noon—and why standard stimulation isn\'t the answer.',
                '<strong>The "Dirty Energy" Trap:</strong> Why sugary drinks and high-acid coffees spike your energy fast, only to drop you even harder (and how to step off the rollercoaster).',
                '<strong>The "Over-Caffeinated" Spiral:</strong> How to spot the difference between being wired and being focused—and the simple switch that supports calm clarity.'
            ]
        },
        {
            name: 'truthLabel',
            type: 'richText',
            defaultValue: 'Here\'s the truth:'
        },
        {
            name: 'truthText',
            type: 'richText',
            defaultValue: 'Your brain isn\'t broken. It just needs better fuel.'
        },
        {
            name: 'descriptionText',
            type: 'richText',
            defaultValue: 'Neuroflow is a revolutionary nootropic mushroom coffee that\'s been scientifically designed to help you think faster, focus longer, and feel mentally unstoppable—without the crash.'
        },
        {
            name: 'userComment',
            type: 'richText',
            defaultValue: '"Mindflow is a great start to the day. It left me energized but also focused, without the jittery feeling of lots of coffees. I appreciate that Lifeboost has added electrolytes, adaptogens, and collagen for the optimal morning beverage. It mixes into water very easily and has a smooth taste! I would recommend it to anyone looking to level up their morning coffee game."'
        },
        {
            name: 'userCommentAuthor',
            type: 'richText',
            defaultValue: '— Jenica Weaver DPT, ATC'
        },
        {
            name: 'heroImageSrc',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            defaultValue: '/assets/images/image1-Photoroom.png'
        },
        {
            name: 'heroImageAlt',
            type: 'text',
            defaultValue: 'LIFEBOOST MINDLFOW INSTANT COFFEE product'
        },
        {
            name: 'buttonText',
            type: 'text',
            defaultValue: 'ORDER NOW'
        },
        {
            name: 'buttonScrollTarget',
            type: 'text',
            defaultValue: 'products'
        }
    ]
})
</script>
